import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import {
  Clock,
  CheckCircle2,
  AlertTriangle,
  FileText,
  ArrowRight,
  ShieldCheck,
  RefreshCw,
  ExternalLink,
} from "lucide-react";
import {
  fetchPartnerUserById,
  fetchUserDocuments,
  fetchMySubscription,
} from "../../api/partner/partnerApi";
import {
  setVerificationStatus,
  setUploadedDocuments,
  setSubscriptionData,
} from "../../features/partnerOnboarding/partnerOnboardingSlice";
import { showToast } from "../../components/common/notification/Notification";
import PartnerLayout from "../../components/partner/PartnerLayout";
import PartnerStepIndicator from "../../components/partner/PartnerStepIndicator";
import "./PartnerPages.css";

export const PartnerVerification = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const authUser = useSelector((state) => state.auth.user);
  const profileServiceId = useSelector((state) => state.partnerOnboarding?.profile?.service_id);
  const token = authUser?.accessToken || authUser?.token || authUser?.refreshToken;
  const userId = authUser?.user?.id || authUser?.userDetails?.id;
  const serviceId = String(
    authUser?.user?.service_id || profileServiceId || "1"
  );

  const { verificationStatus, documents, uploadedDocumentTypes, subscriptionStatus } = useSelector(
    (state) => state.partnerOnboarding
  );

  const [loadingStatus, setLoadingStatus] = useState(true);
  const [partnerDetails, setPartnerDetails] = useState(null);

  const checkStatus = async () => {
    if (!token) return;
    setLoadingStatus(true);

    try {
      // 1. Fetch user status
      const userRes = await fetchPartnerUserById(token, userId);
      const user = userRes?.user || authUser?.user;
      setPartnerDetails(user);

      // 2. Fetch documents
      const docsRes = await fetchUserDocuments(token);
      const docs = docsRes?.documents || docsRes?.data || [];
      dispatch(setUploadedDocuments(docs));

      // 3. Fetch subscription
      try {
        const subRes = await fetchMySubscription(token);
        dispatch(setSubscriptionData(subRes?.subscription || subRes));
      } catch (e) {
        console.warn("Subscription check:", e);
      }

      // Compute verification status:
      // Statuses: NEW, DOCUMENTS_PENDING, UNDER_REVIEW, VERIFIED, EXPIRED_SUBSCRIPTION
      const isVerified = Boolean(user?.active_status || user?.is_verified || user?.status === "VERIFIED");
      const hasDocs = docs.length > 0;

      if (isVerified) {
        dispatch(setVerificationStatus("VERIFIED"));
      } else if (hasDocs) {
        dispatch(setVerificationStatus("UNDER_REVIEW"));
      } else {
        dispatch(setVerificationStatus("DOCUMENTS_PENDING"));
      }
    } catch (err) {
      console.error("Error refreshing partner verification status:", err);
    } finally {
      setLoadingStatus(false);
    }
  };

  useEffect(() => {
    checkStatus();
  }, [token, userId]);

  return (
    <PartnerLayout
      currentStepIndex={6}
      title="You're Almost There"
      subtitle="Track your account review and partner onboarding status in real time."
      visualTitle="Welcome to AHD Partner Hub"
      visualSubtitle="Once approved, your services and property listings will be instantly accessible to thousands of potential customers."
    >
      <div className="partner-form-container">
        {loadingStatus ? (
          <div className="partner-loading-state py-5">
            <div className="spinner-border text-primary mb-3" role="status" />
            <p className="text-muted">Fetching latest verification details...</p>
          </div>
        ) : (
          <div className="partner-step-pane">
            {/* STATE 1: VERIFIED / ACTIVE */}
            {verificationStatus === "VERIFIED" && (
              <div className="partner-status-view">
                <div className="partner-status-view__icon partner-status-view__icon--success">
                  <CheckCircle2 size={42} />
                </div>
                <h2 className="partner-status-view__title">Account Verified & Active!</h2>
                <p className="partner-status-view__desc">
                  Congratulations! Your partner profile has been verified by the All Home Desire team.
                  You can now access your partner dashboard, manage customer leads, and showcase properties.
                </p>

                <div className="partner-status-view__actions">
                  <button
                    type="button"
                    className="partner-btn partner-btn--primary partner-btn--lg"
                    onClick={() => navigate("/partner/workspace")}
                  >
                    <span>Go to Partner Workspace</span>
                    <ArrowRight size={18} />
                  </button>
                </div>
              </div>
            )}

            {/* STATE 2: UNDER REVIEW / VERIFICATION IN PROGRESS */}
            {verificationStatus === "UNDER_REVIEW" && (
              <div className="partner-status-view">
                <div className="partner-status-view__icon partner-status-view__icon--review">
                  <Clock size={42} />
                </div>
                <h2 className="partner-status-view__title">Verification in Progress</h2>
                <p className="partner-status-view__desc">
                  Your KYC documents and profile details have been submitted and are currently under review by our
                  compliance team. Account verification usually takes <strong>24 to 48 hours</strong>.
                </p>

                <div className="partner-review-box">
                  <div className="partner-review-box__item">
                    <ShieldCheck size={18} color="#10b981" />
                    <span>Profile Details: <strong>Submitted</strong></span>
                  </div>
                  <div className="partner-review-box__item">
                    <ShieldCheck size={18} color="#10b981" />
                    <span>KYC Documents: <strong>Under Review</strong></span>
                  </div>
                  <div className="partner-review-box__item">
                    <ShieldCheck size={18} color="#10b981" />
                    <span>Domain: <strong>{partnerDetails?.service?.name || "Service Partner"}</strong></span>
                  </div>
                </div>

                <div className="partner-status-view__actions">
                  <button
                    type="button"
                    className="partner-btn partner-btn--outline"
                    onClick={checkStatus}
                  >
                    <RefreshCw size={15} />
                    <span>Refresh Status</span>
                  </button>
                  <Link to="/partner/workspace" className="partner-btn partner-btn--primary">
                    <span>Partner Workspace</span>
                  </Link>
                </div>
              </div>
            )}

            {/* STATE 3: DOCUMENTS PENDING */}
            {(verificationStatus === "DOCUMENTS_PENDING" || verificationStatus === "NEW") && (
              <div className="partner-status-view">
                <div className="partner-status-view__icon partner-status-view__icon--warning">
                  <FileText size={42} />
                </div>
                <h2 className="partner-status-view__title">Complete Your Verification</h2>
                <p className="partner-status-view__desc">
                  Your partner account requires document submission before our admin team can approve your profile.
                </p>

                <div className="partner-status-view__actions">
                  <button
                    type="button"
                    className="partner-btn partner-btn--primary partner-btn--lg"
                    onClick={() => navigate("/partner/documents")}
                  >
                    <span>Upload Required Documents</span>
                    <ArrowRight size={18} />
                  </button>
                </div>
              </div>
            )}

            {/* STATE 4: EXPIRED SUBSCRIPTION */}
            {subscriptionStatus === "EXPIRED" && (
              <div className="partner-status-view">
                <div className="partner-status-view__icon partner-status-view__icon--warning">
                  <AlertTriangle size={42} />
                </div>
                <h2 className="partner-status-view__title">Subscription Renewal Required</h2>
                <p className="partner-status-view__desc">
                  Your partner subscription has expired. Please renew your plan to continue accessing partner features.
                </p>

                <div className="partner-status-view__actions">
                  <button
                    type="button"
                    className="partner-btn partner-btn--primary partner-btn--lg"
                    onClick={() => navigate("/partner/subscription")}
                  >
                    <span>Renew Subscription</span>
                    <ArrowRight size={18} />
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </PartnerLayout>
  );
};

export default PartnerVerification;
